<template>
  <RevForm
    :id
    class="mt-12"
    :initial-values
    :name="id"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <div class="flex flex-col gap-12">
        <div class="flex flex-col gap-12 md:flex-row">
          <RevInputText
            id="routing-number"
            v-model="values.routingNumber"
            class="mb-20 w-full"
            :description="i18n(translations.routingNumberDescription)"
            :disabled
            :error="errors.routingNumber"
            :label="i18n(translations.routingNumber)"
          />

          <RevInputText
            id="account-number"
            v-model="values.accountNumber"
            class="mb-20 w-full"
            :description="i18n(translations.accountNumberDescription)"
            :disabled
            :error="errors.accountNumber"
            :label="i18n(translations.accountNumber)"
          />
        </div>

        <RevInputText
          id="account-holder"
          v-model="values.accountHolder"
          class="mb-6"
          :disabled
          :error="errors.accountHolder"
          :label="i18n(translations.accountHolder)"
        />
      </div>
    </template>
  </RevForm>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  RevForm,
  makeValidate,
  maxLength,
  minLength,
  required,
} from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'

import { usePaymentAuthorize } from '../../../../api/postPaymentAuthorize/usePaymentAuthorize.api'
import { getRedirectionToPaymentResult } from '../../helpers/getRedirection'
import type {
  PaymentFormAdapterEmits,
  PaymentFormAdapterProps,
  SubmitSuccessData,
} from '../../types/PaymentFormAdapter'
import { PaymentSubmitError } from '../../types/PaymentSubmitError'

import translations from './AchAdapter.translations'

const props = defineProps<PaymentFormAdapterProps>()
const emit = defineEmits<PaymentFormAdapterEmits>()

const i18n = useI18n()
const postPaymentAuthorize = usePaymentAuthorize()

type Values = {
  accountNumber: string
  routingNumber: string
  accountHolder: string
}

const handleSubmit = async (values: Values) => {
  emit('submit-start')
  try {
    const { paymentId } = await props.createPayment(values)
    const { accountNumber, routingNumber, accountHolder } = values

    await postPaymentAuthorize({
      pathParams: { paymentId },
      body: {
        ownerName: accountHolder,
        bankAccount: {
          number: accountNumber,
          locationID: routingNumber,

          // TODO [PAYIN-5119]: Let the user choose the type of account
          type: 'checking',
        },
      },
    })

    emit('submit-success', {
      paymentId,
      redirection: getRedirectionToPaymentResult(paymentId),
    } satisfies SubmitSuccessData)
  } catch (error) {
    emit('submit-error', PaymentSubmitError.fromAnyError(error))
  }
}

const initialValues = computed(
  (): Values => ({
    accountNumber: '',
    routingNumber: '',
    accountHolder: '',
  }),
)

const ACCOUNT_NUMBER_MIN_LENGTH = 4
const ACCOUNT_NUMBER_MAX_LENGTH = 17
const ROUTING_NUMBER_LENGTH = 9

const validate = makeValidate<Values>({
  accountHolder: [required(i18n(translations.required))],
  accountNumber: [
    required(i18n(translations.required)),
    minLength(
      ACCOUNT_NUMBER_MIN_LENGTH,
      i18n(translations.accountNumberError, {
        min: ACCOUNT_NUMBER_MIN_LENGTH,
        max: ACCOUNT_NUMBER_MAX_LENGTH,
      }),
    ),
    maxLength(
      ACCOUNT_NUMBER_MAX_LENGTH,
      i18n(translations.accountNumberError, {
        min: ACCOUNT_NUMBER_MIN_LENGTH,
        max: ACCOUNT_NUMBER_MAX_LENGTH,
      }),
    ),
  ],
  routingNumber: [
    required(i18n(translations.required)),
    minLength(
      ROUTING_NUMBER_LENGTH,
      i18n(translations.routingNumberError, {
        length: ROUTING_NUMBER_LENGTH,
      }),
    ),
    maxLength(
      ROUTING_NUMBER_LENGTH,
      i18n(translations.routingNumberError, {
        length: ROUTING_NUMBER_LENGTH,
      }),
    ),
  ],
})
</script>
